import { Component, Inject, } from "@angular/core";
import { BaseChartDirective } from "ng2-charts";
import { MagicChartBaseComponent } from "./magic-chart-base.component";
import { environment } from "../../../../../environments/environment";
import { debounceTime, Subscription } from "rxjs";
import { DeckCardEvent } from "@ponder-core/events/deck.events";
import { DeckLayerName } from "@apiserver-rest/ponder-data-client";
import { isBlack, isBlue, isColorless, isGreen, isRed, isWhite } from "@ponder-features/magic/card/magicCardUtils";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { ChartDataLabelType } from "@ponder-shared/charts/chart-base.component";

@Component({
    selector: "magic-color-pie-chart",
    imports: [BaseChartDirective],
    templateUrl: "./magic-color-pie-chart.component.html"
})
export class MagicColorPieChartComponent extends MagicChartBaseComponent {
    public summary: string = "Insufficient data available.";
    
    public black: ColorStats = new ColorStats("Black", environment.colors.magic.black, 3);
    public white: ColorStats = new ColorStats("White", environment.colors.magic.white, 1);
    public red: ColorStats = new ColorStats("Red", environment.colors.magic.red, 4);
    public green: ColorStats = new ColorStats("Green", environment.colors.magic.green, 5);
    public blue: ColorStats = new ColorStats("Blue", environment.colors.magic.blue, 2);
    public colorless: ColorStats = new ColorStats("Colorless", environment.colors.magic.colorless, 6);

    private allStats: ColorStats[] = [ this.black, this.white, this.red, this.green, this.blue, this.colorless ];
    private deckCardSub!: Subscription;

    public override ngOnInit(): void {
        this.dataLabel = "Cards";
        this.dataLabelType = ChartDataLabelType.Text;
        this.largeScreenAspectRatio = 1;
        this.smallScreenAspectRatio = 1;
        this.xGridLineVisible = false;
        this.yGridLineVisible = false;
        this.xGridBorderVisible = false;
        this.yGridBorderVisible = false;
        this.xGridTickVisible = false;
        this.yGridTickVisible = false;
        this.lineWidth = 0;
        this.lineBorderWidth = 0;

        super.ngOnInit();
        this.updateData();

        this.deckCardSub = this.events.deck.deckCardEvent
            .pipe(debounceTime(500))
            .subscribe({
                next: (event: DeckCardEvent) => {
                    if (event.layer === DeckLayerName.Mainboard) {
                        this.updateData();
                    }
                }
            });
    }

    public ngOnDestroy(): void {
        this.deckCardSub.unsubscribe();
    }

    private updateData(): void {
        var totalCount = 0;
        this.allStats.forEach(s => s.reset());
            
        // ---------------------------------------------------------------------------------
        // Compile Data

        this.deckComponent.mainLayer.cards?.forEach(c => {
            var magicCard = c.card.item.data?.magic;
            var count = c.count + c.specialCount;

            if (magicCard) {
                if (magicCard.faces![0].typeLine!.indexOf('Land') >= 0) {
                    return;
                }

                this.white.increment(isWhite(magicCard) ? count : 0);
                this.blue.increment(isBlue(magicCard) ? count : 0);
                this.black.increment(isBlack(magicCard) ? count : 0);
                this.red.increment(isRed(magicCard) ? count : 0);
                this.green.increment(isGreen(magicCard) ? count : 0);
                this.colorless.increment(isColorless(magicCard) ? count : 0);

                totalCount += count;
            }
        });

        this.black.calculate(totalCount);
        this.white.calculate(totalCount);
        this.red.calculate(totalCount);
        this.green.calculate(totalCount);
        this.blue.calculate(totalCount);
        this.colorless.calculate(totalCount);
        
        // ---------------------------------------------------------------------------------
        // Sort least to greatest (which looks better in the chart)
        
        this.allStats.sort((a, b) => { 
            if (a.count == b.count) {
                return (a.tieBreaker > b.tieBreaker) ? -1 : 1;
            } else {
                return (a.count < b.count) ? -1 : 1; 
            }
        });
        
        // ---------------------------------------------------------------------------------
        // Add to chart

        this.resetData();
        this.allStats.forEach(stats => {
            if (stats.count) {
                this.addDataPoint(stats.count, stats.label, stats.color);
            }
        });

        // ---------------------------------------------------------------------------------
        // Sort greatest to least for the summary
        
        this.allStats.reverse();
        this.composeSummary();
    }

    private composeSummary(): void {
        this.summary = "Insufficient data available.";

        if (this.allStats[0].count) {

            if (this.allStats[1].count) {
                this.summary = `${this.allStats[0].percentage}% of the cards are ${this.allStats[0].lowerLabel}`;

                if (this.allStats[2].count) {
                    for (let i = 1; i < this.allStats.length; ++i) {
                        var currStat = this.allStats[i];

                        if (!currStat.count) {
                            break;
                        }

                        var doesNextHaveCount = ((i < this.allStats.length - 1) && this.allStats[i + 1].count) ? true : false;

                        if (doesNextHaveCount) {
                            this.summary += `, ${currStat.percentage}% are ${currStat.lowerLabel}`;
                        } else {
                            this.summary += `, and ${currStat.percentage}% are ${currStat.lowerLabel}.`;
                        }
                    }
                } else {
                    // Only two colors
                    this.summary += ` and ${this.allStats[1].percentage}% are ${this.allStats[1].lowerLabel}.`;
                }
            } else {
                // Only one color.
            this.summary = `${this.allStats[0].percentage}% of the cards are ${this.allStats[0].lowerLabel}.`;
            }
        }
    }
}

class ColorStats {
    public count: number = 0;
    public percentage: number = 0;
    public lowerLabel!: string;

    public constructor(public label: string, public color: string, public tieBreaker: number) {
        this.lowerLabel = label.toLowerCase();
    }

    public reset(): void {
        this.count = 0;
        this.percentage = 0;
    }

    public increment(count: number): void {
        this.count += count;
    }

    public calculate(total: number): void {
        if (total) {
            this.percentage = Math.round((this.count / total) * 100);
        } else {
            this.percentage = 0;
        }
    }
}